import { ModuleTypeEnum, ModuleColumnDefinition } from '../module';
import { FieldTypeEnum } from '../custom-field.types';
import { Member, MemberStatusEnum, MEMBER_STATUS_COLORS, MEMBER_STATUS_DISPLAY_NAMES } from '../member';

/**
 * Standard width categories for consistent column sizing across member tables
 */
const MEMBER_COLUMN_WIDTHS = {
  // Very narrow - icons, small numbers, boolean indicators
  VERY_NARROW: { width: '50px', minWidth: '35px' },
  // Narrow - short text, priorities, small dropdowns
  NARROW: { width: '120px', minWidth: '80px' },
  // Medium - standard text fields, dates, status fields
  MEDIUM: { width: '160px', minWidth: '120px' },
  // Wide - longer text fields, descriptions
  WIDE: { width: '200px', minWidth: '140px' },
  // Very wide - names, main content
  VERY_WIDE: { width: '280px', minWidth: '200px' },
} as const;

/**
 * Creates standard column definitions for the Member module
 */
export function createMemberStandardColumns(): ModuleColumnDefinition[] {
  return [
    {
      id: 'name',
      name: 'name' as keyof Member,
      label: 'Name',
      description: "Team member's full name (first and last name)",
      isVisible: true,
      isRequired: true,
      isSystem: true,
      isFixed: true,
      order: 0,
      fieldType: FieldTypeEnum.enum.STRING,
      ...MEMBER_COLUMN_WIDTHS.WIDE,
    },
    {
      id: 'firstName',
      name: 'firstName' as keyof Member,
      label: 'First Name',
      description: "Team member's first name",
      isVisible: false, // Hidden from table, but available in detail view
      isRequired: false,
      isSystem: true,
      isFixed: false,
      order: 0.1, // After name but before email in detail views
      fieldType: FieldTypeEnum.enum.STRING,
      ...MEMBER_COLUMN_WIDTHS.MEDIUM,
    },
    {
      id: 'lastName',
      name: 'lastName' as keyof Member,
      label: 'Last Name',
      description: "Team member's last name",
      isVisible: false, // Hidden from table, but available in detail view
      isRequired: false,
      isSystem: true,
      isFixed: false,
      order: 0.2, // After firstName in detail views
      fieldType: FieldTypeEnum.enum.STRING,
      ...MEMBER_COLUMN_WIDTHS.MEDIUM,
    },
    {
      id: 'email',
      name: 'email' as keyof Member,
      label: 'Email',
      description: "Team member's email address",
      isVisible: true,
      isRequired: true,
      isSystem: true,
      isFixed: false,
      order: 1,
      fieldType: FieldTypeEnum.enum.STRING,
      ...MEMBER_COLUMN_WIDTHS.WIDE,
    },
    {
      id: 'jobTitle',
      name: 'jobTitle' as keyof Member,
      label: 'Job Title',
      description: "Team member's job title",
      isVisible: true,
      isRequired: false,
      isSystem: false,
      isFixed: false,
      order: 2,
      fieldType: FieldTypeEnum.enum.STRING,
      ...MEMBER_COLUMN_WIDTHS.MEDIUM,
    },
    {
      id: 'department',
      name: 'department' as keyof Member,
      label: 'Department',
      description: "Team member's department",
      isVisible: true,
      isRequired: false,
      isSystem: false,
      isFixed: false,
      order: 3,
      fieldType: FieldTypeEnum.enum.DROPDOWN,
      ...MEMBER_COLUMN_WIDTHS.MEDIUM,
    },
    {
      id: 'reportsToId',
      name: 'reportsToId' as keyof Member,
      label: 'Reports To',
      description: 'The manager this member reports to',
      isVisible: false,
      isRequired: false,
      isSystem: false,
      isFixed: false,
      order: 3.5,
      fieldType: FieldTypeEnum.enum.USER,
      ...MEMBER_COLUMN_WIDTHS.MEDIUM,
    },
    {
      id: 'role',
      name: 'role' as keyof Member,
      label: 'System Role',
      description: "Team member's system role",
      isVisible: true,
      isRequired: true,
      isSystem: true,
      isFixed: false,
      order: 4,
      fieldType: FieldTypeEnum.enum.DROPDOWN,
      ...MEMBER_COLUMN_WIDTHS.NARROW,
    },
    {
      id: 'status',
      name: 'status' as keyof Member,
      label: 'Status',
      description: 'Member status (active, inactive, pending, etc.)',
      isVisible: true,
      isRequired: true,
      isSystem: true,
      isFixed: false,
      order: 5,
      fieldType: FieldTypeEnum.enum.DROPDOWN,
      ...MEMBER_COLUMN_WIDTHS.NARROW,
      options: Object.values(MemberStatusEnum).map(status => ({
        value: status,
        label: MEMBER_STATUS_DISPLAY_NAMES[status],
        color: MEMBER_STATUS_COLORS[status],
      })),
    },
    {
      id: 'employmentType',
      name: 'employmentType' as keyof Member,
      label: 'Employment',
      description: 'Employment type (Full Time, Part Time, etc.)',
      isVisible: true,
      isRequired: false,
      isSystem: false,
      isFixed: false,
      order: 6,
      fieldType: FieldTypeEnum.enum.DROPDOWN,
      ...MEMBER_COLUMN_WIDTHS.NARROW,
    },
    {
      id: 'weeklyCapacity',
      name: 'weeklyCapacity' as keyof Member,
      label: 'Capacity',
      description: 'Weekly capacity in hours',
      isVisible: false,
      isRequired: false,
      isSystem: false,
      isFixed: false,
      order: 7,
      fieldType: FieldTypeEnum.enum.NUMBER,
      ...MEMBER_COLUMN_WIDTHS.NARROW,
    },
    {
      id: 'targetUtilization',
      name: 'targetUtilization' as keyof Member,
      label: 'Target %',
      description: 'Percentage of capacity hours that should be utilized',
      isVisible: false,
      isRequired: false,
      isSystem: false,
      isFixed: false,
      order: 8,
      fieldType: FieldTypeEnum.enum.NUMBER,
      ...MEMBER_COLUMN_WIDTHS.NARROW,
    },
    {
      id: 'targetBillablePercent',
      name: 'targetBillablePercent' as keyof Member,
      label: 'Billable %',
      description: 'Percentage of utilized hours that should be billable',
      isVisible: false,
      isRequired: false,
      isSystem: false,
      isFixed: false,
      order: 8.2,
      fieldType: FieldTypeEnum.enum.NUMBER,
      ...MEMBER_COLUMN_WIDTHS.NARROW,
    },
    {
      id: 'createdAt',
      name: 'createdAt' as keyof Member,
      label: 'Joined',
      description: 'Date when member was added',
      isVisible: false,
      isRequired: false,
      isSystem: true,
      isFixed: false,
      order: 9,
      fieldType: FieldTypeEnum.enum.DATETIME,
      ...MEMBER_COLUMN_WIDTHS.MEDIUM,
    },
    {
      // Plan 9c T32: optional weighted-margin column — hidden by default (progressive disclosure)
      id: 'weightedMargin',
      name: 'weightedMargin',
      label: 'Weighted Margin',
      description: 'Weighted margin percentage based on approved time (Financial Actuals)',
      isVisible: false,
      isRequired: false,
      isSystem: false,
      isFixed: false,
      order: 10,
      fieldType: FieldTypeEnum.enum.NUMBER,
      ...MEMBER_COLUMN_WIDTHS.NARROW,
    },
  ];
}

/**
 * Default standard column definitions for the Member module
 */
export const MEMBER_STANDARD_COLUMNS: ModuleColumnDefinition[] = createMemberStandardColumns();

/**
 * Helper function to get all available columns for members,
 * combining standard and custom fields
 */
export function getMemberColumns(
  customFields: any[] = [],
  visibilityPreferences?: Record<string, boolean>
): ModuleColumnDefinition[] {
  // Start with standard columns
  const standardColumns = createMemberStandardColumns().map((column) => ({
    ...column,
    // Override visibility if preferences exist
    isVisible:
      visibilityPreferences?.[column.id] !== undefined
        ? !!visibilityPreferences[column.id]
        : column.isVisible,
  }));

  // Default width for custom field columns based on field type
  const getCustomFieldWidth = (fieldType: string): { width: string; minWidth: string } => {
    switch (fieldType) {
      case FieldTypeEnum.enum.STRING:
        return MEMBER_COLUMN_WIDTHS.WIDE;
      case FieldTypeEnum.enum.TEXT:
        return MEMBER_COLUMN_WIDTHS.WIDE;
      case FieldTypeEnum.enum.NUMBER:
        return MEMBER_COLUMN_WIDTHS.NARROW;
      case FieldTypeEnum.enum.BOOLEAN:
        return MEMBER_COLUMN_WIDTHS.NARROW;
      case FieldTypeEnum.enum.DATE:
      case FieldTypeEnum.enum.DATETIME:
        return MEMBER_COLUMN_WIDTHS.MEDIUM;
      case FieldTypeEnum.enum.DROPDOWN:
        return MEMBER_COLUMN_WIDTHS.MEDIUM;
      case FieldTypeEnum.enum.FILE:
        return MEMBER_COLUMN_WIDTHS.WIDE;
      case FieldTypeEnum.enum.USER:
        return MEMBER_COLUMN_WIDTHS.MEDIUM;
      default:
        return MEMBER_COLUMN_WIDTHS.MEDIUM;
    }
  };

  // Map custom fields to column definitions
  const customColumns = customFields.map((field, index) => {
    const widthSettings = getCustomFieldWidth(field.fieldType);

    return {
      id: `custom_${field.fieldName}`,
      name: field.fieldName,
      label: field.label,
      description: `Custom field: ${field.label}`,
      isVisible:
        visibilityPreferences?.[`custom_${field.fieldName}`] !== undefined
          ? !!visibilityPreferences[`custom_${field.fieldName}`]
          : !!field.isActive,
      isRequired: false,
      isSystem: false,
      order: 91 + index,
      fieldType: field.fieldType,
      width: widthSettings.width,
      minWidth: widthSettings.minWidth,
    };
  }) as ModuleColumnDefinition[];

  // Combine both types of columns
  return [...standardColumns, ...customColumns];
}

/**
 * Get module type for member column definitions
 */
export function getMemberModuleType(): ModuleTypeEnum {
  return ModuleTypeEnum.MEMBER;
}
